import type { CustomSectionFieldPayload, CustomSectionPayload } from '@/api/customElements';
import type { FormSection, WorkflowField, WorkflowFieldPropertiesPayload } from '@/api/workflows';

/**
 * Field property keys that must NEVER be carried into a saved custom element,
 * regardless of context. Empty today: cross-reference properties (conditions,
 * sourceField) are handled by the fixer stage in `conditionStripping.ts`, which
 * runs on the domain object BEFORE this mapper — so they're already resolved by
 * the time we copy. This denylist runs AFTER that stage, as the escape hatch for
 * any property that must be dropped unconditionally (add a key here only then).
 */
const FIELD_PROPERTY_DENYLIST = new Set<keyof WorkflowFieldPropertiesPayload>([]);

/**
 * Maps a live (already-stripped) WorkflowField into the nested field shape a
 * Section/Step custom-element payload carries. Copy-all: every property key falls
 * through into `properties` by default (the same WorkflowFieldPropertiesPayload
 * shape `workflowsApi.placeField`/`editField` use), so a newly-added self-contained
 * field property is carried automatically instead of being silently dropped. Only
 * the handful of top-level wire keys are peeled off, and the denylist gets the last
 * word. Cross-reference properties are pre-resolved by the fixer stage upstream.
 */
function fieldToPayload(field: WorkflowField): CustomSectionFieldPayload {
  const { id, type, labelEn, labelFr, required, conditions, conditionCombinator, ...rest } = field;

  // Fresh copy — safe to mutate. `rest` carries a few structurally-wider keys
  // (`options`, `repeatableTemplate`) than the payload models, matching the
  // previous direct pass-through of those keys; the cast bridges that.
  const properties = { ...rest } as WorkflowFieldPropertiesPayload;
  for (const key of FIELD_PROPERTY_DENYLIST) {
    delete (properties as Record<string, unknown>)[key];
  }

  return { id, type, labelEn, labelFr, required, conditions, conditionCombinator, properties };
}

/** Maps a live (already-stripped) FormSection into the wire shape `customElementsApi.saveSection`/`saveStep` expect. */
export function sectionToPayload(section: FormSection): CustomSectionPayload {
  return {
    titleEn: section.titleEn,
    titleFr: section.titleFr,
    conditionCombinator: section.conditionCombinator,
    conditions: section.conditions,
    rows: section.rows.map((row) => ({
      columns: row.columns.map((column) => ({
        field: column.field ? fieldToPayload(column.field) : null,
      })),
    })),
  };
}
